import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { map, Observable, switchMap, tap } from 'rxjs';
import { SubSink } from 'subsink';

import {
  MarketingDataViewModel,
  MerchantSiteViewModel,
  TourismServiceViewModel,
} from '@app/api/models';
import { ImageUploadRequest } from '@image-upload/interfaces/image-upload-request.interface';
import { ImageUploadResponse } from '@image-upload/interfaces/image-upload-response.interface';
import {
  MerchantActions,
  MerchantSelectors,
} from '@merchant/store/merchant.slice';
import { LocalizedTourismServiceSubformComponent } from '@shared/components/localized-tourism-service-subform/localized-tourism-service-subform.component';
import { LocalizedValueSubformComponent } from '@shared/components/localized-value-subform/localized-value-subform.component';
import {
  getServerErrorForField,
  logInvalidControls,
} from '@shared/helpers/form-helper';
import { trimInput } from '@shared/helpers/input-trim-helper';
import { ApiErrorResponse } from '@shared/interfaces/api-error-response';
import { LocalizedTitle } from '@shared/interfaces/merchant-shared';
import { EmbeddableValidator } from '@shared/validators/embeddable.validator';

@Component({
  selector: 'app-merchant-marketing-data-edit',
  templateUrl: './merchant-marketing-data-edit.component.html',
  styleUrls: ['./merchant-marketing-data-edit.component.scss'],
})
export class MerchantMarketingDataEditComponent implements OnInit, OnDestroy {
  TITLE = 'MERCHANT.TITLE.';
  PLACEHOLDER = 'MERCHANT.PLACEHOLDER.';
  HINT = 'MERCHANT.HINT.';
  REQUIRED = this.HINT + 'REQUIRED';

  logoUploadEnabled = false;

  showVisibleFields = false;

  error$: Observable<ApiErrorResponse | undefined> = this.store.select(
    MerchantSelectors.selectMarketingDataEditError
  );
  isMarketingDataLoaded$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectMarketingDataLoaded
  );
  areSitesLoaded$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectSitesLoaded
  );
  sites$: Observable<MerchantSiteViewModel[]> = this.store.select(
    MerchantSelectors.selectSiteList
  );
  profileImageUrl$: Observable<string | null> = this.store.select(
    MerchantSelectors.selectMerchantProfileImageUrl
  );
  isProfileImageUploading$: Observable<boolean> = this.store.select(
    MerchantSelectors.selectMerchantProfileImageUploading
  );

  merchantProfileImage$: Observable<ImageUploadResponse | null> =
    this.store.select(MerchantSelectors.selectMerchantProfileImage);

  private subs = new SubSink();

  form: UntypedFormGroup = this.fb.group({
    id: [null], // hidden
    merchantSiteIds: [null, Validators.required],
    profilePictureId: [null],
    marketingVideo: [null, null, [this.embeddableValidator.isEmbeddable]],
    introduction: LocalizedValueSubformComponent.buildDefaultForm(),
    tourismServices: this.fb.array([]),
    promotions: this.fb.array([]),
    visibleFields: this.fb.group({
      merchantSiteIds: new UntypedFormControl({ value: true, disabled: true }),
      profilePictureId: [false],
      marketingVideo: [false],
      introduction: [false],
      tourismServices: [false],
      promotions: [false],
    }),
  });

  constructor(
    private readonly store: Store,
    private fb: UntypedFormBuilder,
    private readonly route: ActivatedRoute,
    private readonly location: Location, // from @angular/common
    readonly router: Router,
    private readonly embeddableValidator: EmbeddableValidator
  ) {}

  ngOnInit() {
    this.store.dispatch(MerchantActions.resetMerchantImage());

    this.subs.sink = this.store
      .select(MerchantSelectors.isBasicDataLoaded)
      .subscribe((res) => {
        if (res) {
          this.subs.sink = this.areSitesLoaded$.subscribe((loaded) => {
            if (!loaded) {
              this.store.dispatch(MerchantActions.loadSites.trigger());
            }
          });
          this.subs.sink = this.isMarketingDataLoaded$.subscribe((loaded) => {
            if (!loaded) {
              this.store.dispatch(MerchantActions.loadMarketingData.trigger());
            }
          });

          this.subs.sink = this.route.queryParams
            .pipe(
              map((params) => params['id']),
              switchMap((id?: string) =>
                this.store.select(
                  MerchantSelectors.selectSingleMarketingData(id)
                )
              ),
              tap((data) => (data ? this.onData(data) : null))
            )
            .subscribe();
        }
      });

    this.subs.sink = this.merchantProfileImage$.subscribe((res) => {
      if (res) {
        this.form.controls['profilePictureId'].setValue(res.id);
      }
    });

    this.subs.sink = this.route.queryParams.subscribe((params) => {
      if (!params['id']) {
        this.tourismServiceArray.push(
          LocalizedTourismServiceSubformComponent.buildDefaultForm()
        );
        this.promotionArray.push(
          LocalizedValueSubformComponent.buildDefaultForm()
        );
      }
    });
  }

  onData(data: MarketingDataViewModel) {
    let initialFormValue = this.form.value;
    this.form.reset(initialFormValue);
    this.form.patchValue({
      ...data,
      visibleFields: {
        ...data.visibleFields,
        merchantSiteIds: true,
      },
    }); // can't use setControl(), must fill existing array with new items

    this.promotionArray?.reset();
    data.promotions.forEach((promotion: LocalizedTitle) => {
      this.promotionArray.push(
        LocalizedValueSubformComponent.buildForm(promotion)
      );
    });

    if (!data.promotions.length) {
      this.promotionArray.push(
        LocalizedValueSubformComponent.buildDefaultForm()
      );
    }

    this.tourismServiceArray?.reset();
    data.tourismServices.forEach(
      (service: { [key: string]: TourismServiceViewModel }) => {
        this.tourismServiceArray.push(
          LocalizedTourismServiceSubformComponent.buildForm(service)
        );
      }
    );

    if (!data.tourismServices.length) {
      this.tourismServiceArray.push(
        LocalizedTourismServiceSubformComponent.buildDefaultForm()
      );
    }

    if (data.profilePictureId) {
      this.getMerchantProfileImage(data.profilePictureId);
    }
  }

  onProfileImageSelected(file: File) {
    const formData: FormData = new FormData();

    formData.append('file', file, file.name);

    const imageUploadRequest: ImageUploadRequest = {
      data: formData,
    };

    this.store.dispatch(
      MerchantActions.uploadMerchantProfileImage.trigger({
        ...imageUploadRequest,
      })
    );
    this.form.controls['profilePictureId'].markAsDirty();
  }

  onRemovePicture(): void {
    const profilePictureId: string =
      this.form.controls['profilePictureId'].value;

    this.store.dispatch(
      MerchantActions.deleteMerchantProfileImage.trigger({
        id: profilePictureId,
      })
    );
    this.form.controls['profilePictureId'].setValue(null);
    this.form.controls['profilePictureId'].markAsDirty();
  }

  onSubmit() {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.store.dispatch(
        MerchantActions.editMarketingData.trigger(trimInput(this.form.value))
      );
    } else {
      logInvalidControls(this.form);
    }
  }

  onToggleVisibleFields() {
    this.showVisibleFields = !this.showVisibleFields;
  }

  onCancel() {
    this.location.back();
  }

  getServerError(fieldName: string): any {
    return getServerErrorForField(this.form, fieldName);
  }

  getControl(fieldName: string): any {
    return this.form?.get(fieldName);
  }

  get tourismServiceGroups(): UntypedFormGroup[] {
    return this.tourismServiceArray.controls as UntypedFormGroup[];
  }

  get tourismServiceArray(): UntypedFormArray {
    return this.form.get('tourismServices') as UntypedFormArray;
  }

  get promotionGroups(): UntypedFormGroup[] {
    return this.promotionArray.controls as UntypedFormGroup[];
  }

  get promotionArray(): UntypedFormArray {
    return this.form.get('promotions') as UntypedFormArray;
  }

  onAddTourismService() {
    this.tourismServiceArray.markAsDirty();
    this.tourismServiceArray.push(
      LocalizedTourismServiceSubformComponent.buildDefaultForm()
    );
  }

  onDeleteTourismService(index: number) {
    this.tourismServiceArray.markAsDirty();
    this.tourismServiceArray.removeAt(index);
  }

  onAddPromotion() {
    this.promotionArray.markAsDirty();
    this.promotionArray.push(LocalizedValueSubformComponent.buildDefaultForm());
  }

  onDeletePromotion(index: number) {
    this.promotionArray.markAsDirty();
    this.promotionArray.removeAt(index);
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private getMerchantProfileImage(pictureId: string): void {
    this.store.dispatch(
      MerchantActions.getMerchantProfileImage.trigger({ id: pictureId })
    );
  }
}
